import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import venueService from '../services/venueService';
import '../styles/venues-map.css';

const VenuesMap = () => {
  const { /* user, */ updateExtendedProfile } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  // Cargar el script de Google Maps
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setMapLoaded(true);
      };
      script.onerror = () => {
        setError('Error al cargar el mapa de Google');
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  // Obtener la ubicación del usuario
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Error obteniendo la ubicación:', error);
          // Ubicación por defecto (Madrid)
          setUserLocation({ lat: 40.416775, lng: -3.70379 });
        },
      );
    } else {
      console.error('Geolocalización no soportada por este navegador');
      // Ubicación por defecto (Madrid)
      setUserLocation({ lat: 40.416775, lng: -3.70379 });
    }
  }, []);

  // Cargar las sedes
  useEffect(() => {
    const fetchVenues = async () => {
      setLoading(true);
      try {
        const venuesData = await venueService.getActiveVenues();
        const activeVenues = Array.isArray(venuesData)
          ? venuesData.filter((venue) => venue.active)
          : venuesData?.venues?.filter((venue) => venue.active) || [];

        setVenues(activeVenues);
      } catch (error) {
        console.error('Error al cargar las sedes:', error);
        setError('Error al cargar las sedes');
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  // Inicializar el mapa cuando se cargue el script y tengamos las sedes
  useEffect(() => {
    if (mapLoaded && userLocation && venues.length > 0 && mapRef.current) {
      const google = window.google;
      const map = new google.maps.Map(mapRef.current, {
        center: userLocation,
        zoom: 12,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });

      googleMapRef.current = map;

      // Añadir marcador para la ubicación del usuario
      new google.maps.Marker({
        position: userLocation,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        title: 'Tu ubicación',
      });

      // Añadir marcadores para las sedes
      venues.forEach((venue) => {
        if (venue.lat && venue.lng) {
          const marker = new google.maps.Marker({
            position: { lat: parseFloat(venue.lat), lng: parseFloat(venue.lng) },
            map,
            title: venue.name,
            animation: google.maps.Animation.DROP,
          });

          // Crear ventana de información
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div class="venue-info-window">
                <h3>${venue.name}</h3>
                <p>${venue.address.street}, ${venue.address.city}</p>
                <p><strong>Valoración:</strong> ${venue.rating || '4.5'} ⭐</p>
              </div>
            `,
          });

          // Añadir evento de clic al marcador
          marker.addListener('click', () => {
            // Cerrar todas las ventanas de información abiertas
            markersRef.current.forEach((m) => m.infoWindow.close());
            
            // Abrir la ventana de información de este marcador
            infoWindow.open(map, marker);
            
            // Seleccionar esta sede
            setSelectedVenue(venue);
          });

          // Guardar referencia al marcador y su ventana de información
          markersRef.current.push({ marker, infoWindow, venue });
        }
      });

      // Ajustar el zoom para mostrar todos los marcadores
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(userLocation);
      venues.forEach((venue) => {
        if (venue.lat && venue.lng) {
          bounds.extend({ lat: parseFloat(venue.lat), lng: parseFloat(venue.lng) });
        }
      });
      map.fitBounds(bounds);
    }
  }, [mapLoaded, userLocation, venues]);

  // Seleccionar una sede
  const handleSelectVenue = async () => {
    if (!selectedVenue) {
      setError('Por favor, selecciona una sede');
      return;
    }

    try {
      // Actualizar el perfil del usuario con la sede seleccionada
      await updateExtendedProfile({
        extendedProfile: {
          venue: selectedVenue._id || selectedVenue.id
        }
      });

      setUpdateSuccess(true);
      
      // Mostrar mensaje de éxito durante 2 segundos y luego redirigir
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (error) {
      console.error('Error al seleccionar la sede:', error);
      setError('Error al seleccionar la sede');
    }
  };

  // Seleccionar una sede de la lista
  const handleVenueClick = (venue) => {
    setSelectedVenue(venue);
    
    // Centrar el mapa en la sede seleccionada
    if (googleMapRef.current && venue.lat && venue.lng) {
      googleMapRef.current.setCenter({ lat: parseFloat(venue.lat), lng: parseFloat(venue.lng) });
      googleMapRef.current.setZoom(15);
      
      // Abrir la ventana de información del marcador correspondiente
      const markerInfo = markersRef.current.find(
        (m) => (m.venue._id || m.venue.id) === (venue._id || venue.id),
      );
      
      if (markerInfo) {
        // Cerrar todas las ventanas de información abiertas
        markersRef.current.forEach((m) => m.infoWindow.close());
        
        // Abrir la ventana de información de este marcador
        markerInfo.infoWindow.open(googleMapRef.current, markerInfo.marker);
      }
    }
  };

  if (loading) {
    return (
      <div className="venues-map-container">
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Cargando sedes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="venues-map-page">
      <div className="container">
        <div className="venues-map-header">
          <h1>Sedes Disponibles</h1>
          <p>Selecciona tu sede preferida para jugar</p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {updateSuccess && (
          <div className="alert alert-success">
            ¡Sede seleccionada correctamente! Redirigiendo al perfil...
          </div>
        )}

        <div className="venues-map-content">
          <div className="venues-list">
            <h2>Listado de Sedes</h2>
            <div className="venues-list-container">
              {venues.length === 0 ? (
                <p className="no-venues">No hay sedes disponibles</p>
              ) : (
                venues.map((venue) => (
                  <div
                    key={venue._id || venue.id}
                    className={`venue-item ${
                      selectedVenue && (selectedVenue._id || selectedVenue.id) === (venue._id || venue.id)
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() => handleVenueClick(venue)}
                  >
                    <div className="venue-item-image">
                      <img
                        src={venue.mainImage || '/img/default-venue.jpg'}
                        alt={venue.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/img/default-venue.jpg';
                        }}
                      />
                    </div>
                    <div className="venue-item-info">
                      <h3>{venue.name}</h3>
                      <p>
                        <span className="material-icons">place</span>
                        {venue.address.city}
                      </p>
                      <p>
                        <span className="material-icons">star</span>
                        {venue.rating || '4.5'} ({venue.reviewsCount || '0'} reseñas)
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="venues-map">
            <div ref={mapRef} className="map-container"></div>
          </div>
        </div>

        <div className="venues-map-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/profile')}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSelectVenue}
            disabled={!selectedVenue || updateSuccess}
          >
            {updateSuccess ? 'Sede Seleccionada' : 'Seleccionar Sede'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VenuesMap;
